import { ArrowPathIcon, CheckCircleIcon, Cog6ToothIcon, PlusIcon, WindowIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { apiRequest } from "src/async/apiUtils";
import Button from "src/components/Shared/Buttons/Button";
import Input from "src/components/Shared/Forms/Inputs/Input";
import Image from "src/components/Shared/Image";
import Members from "src/components/Shared/Members";
import QuickMenu from "src/components/Shared/QuickMenu";
import WorkspaceGroupAuthorizeUserComponent from "src/components/Workspaces/Workspace/Groups/WorkspaceGroupAuthorizeUserComponent";
import { noFavicon } from "src/config/host";
import { isSVGString } from "src/helpers";
import { authorizeUserComponentAccess } from "src/helpers/authorizeUserComponentAccess";
import { classNames } from "src/helpers/classNames";
import useDebounce from "src/hooks/useDebounce";
import { v4 } from "uuid";

const WorkspaceGroupItem = ({
  viewOnly = false,
  defaultGroup = {},
  refreshGroups = () => {},
  pageOptions = [],
  operatorOptions = [],
  workspaceId = null,
  workspaceDetails = {},
  trustedAttributeArr = [],
  valueType = "",
  setSelectedGroup = () => {},
  setGroupChangeWarning = () => {},
  setDeleteGroup = () => {},
  setCloneGroup = () => {},
  setAddIconGroup = () => {},
  userModalOpen = () => {},
  setGroups = () => {},
  groupChanges = 0,
  setGroupChanges = () => {},
  ...props
}) => {
  const [items, setItems] = useState([]);
  const [group, setGroup] = useState({});
  const [name, setName] = useState("");
  const pageJson = {
    id: v4(),
    _id: null,
    page_id: null,
    filters: [],
    is_draft: true,
  };
  const loadingColors = {
    IDLE: "group-focus:text-transparent",
    WAITING: "group-focus:text-gray-700/50",
    DONE: "group-focus:text-green-700/50",
  };

  useEffect(() => {
    setName(defaultGroup?.name);
  }, [defaultGroup?.name]);

  // *** FUNCTIONS ***
  const saveGroupChanges = async () => {
    try {
      const { data } = await apiRequest(
        "put",
        `/workspaces/:workspace_id/groups/:group_id`,
        {
          body: {
            ...group,
            name,
            permissions: group.permissions,
          },
          params: { workspace_id: workspaceId, group_id: group._id },
        },
        { showToastMessage: true, onSuccess: () => {}, onFailure: () => {} },
      );
      setSelectedGroup({
        ...group,
        filters: group?.filters?.map((filter) => ({
          ...filter,
          addWhere: filter?.datasource_id?.trim() ? true : false,
        })),
        permissions: group.permissions.map((permission, index) => {
          let newData = data.data.find((dt, i) => i === index);
          return {
            ...permission,
            datasource_id: permission?.datasource_id?.trim(),
            page_alias: permission?.page_alias?.trim(),
            addWhere: permission?.datasource_id?.trim() ? true : false,
            displayNameShow: permission?.page_alias?.trim() ? true : false,
            _id: newData?._id,
          };
        }),
      });
      refreshGroups();
      setGroupChanges(0);
    } catch (error) {}
  };

  const onClear = () => {
    setGroup({
      ...defaultGroup,
      filters: defaultGroup?.filters?.map((filter) => ({
        ...filter,
        addWhere: filter?.datasource_id?.trim() ? true : false,
      })),
      permissions: defaultGroup?.permissions?.map((permission) => {
        return {
          ...permission,
          addWhere: permission?.datasource_id ? true : false,
          displayNameShow: permission?.page_alias ? true : false,
          displayNameChanges: true,
          filters: permission.filters.map((filter) => {
            return {
              ...filter,
              addWhere: filter?.datasource_id ? true : false,
            };
          }),
        };
      }),
    });
    setGroupChanges(0);
  };

  const addFilter = async () => {
    const uniqueId = v4();
    const filter = {
      _id: uniqueId,
      operator_id: null,
      column_name: null,
      value_type: "value",
      column_value: null,
      trusted_attribute: null,
      datasource_id: null,
      addWhere: false,
    };
    setGroup({ ...group, filters: group?.filters ? [...group.filters, filter] : [filter] });
    setGroupChanges(groupChanges + 1);
  };

  // *** useEffect TRIGGERS ***
  useEffect(() => {
    setName(defaultGroup.name || "");
    setGroup({
      ...defaultGroup,
      filters: defaultGroup?.filters?.map((filter) => ({
        ...filter,
        addWhere: filter?.datasource_id?.trim() ? true : false,
      })),
      permissions: defaultGroup?.permissions?.map((permission) => {
        return {
          ...permission,
          id: v4(),
          addWhere: permission?.datasource_id ? true : false,
          displayNameShow: permission?.page_alias ? true : false,
          displayNameChanges: true,
          filters: permission.filters.map((filter) => {
            return {
              ...filter,
              addWhere: filter?.datasource_id ? true : false,
            };
          }),
        };
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultGroup]);

  useEffect(() => {
    let defaultItems = [];
    if (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["content_page_update"], true)) {
      defaultItems.push({
        name: "Add page",
        onClick: () => {
          setGroup({ ...group, permissions: [...group.permissions, pageJson] });
        },
      });
    }
    if (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["content_page_update"], true)) {
      defaultItems.push({
        name: "Apply group condition",
        onClick: () => {
          addFilter();
        },
      });
    }
    if (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"], true) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["user_update"], true)) {
      defaultItems.push({
        name: "Manage users",
        onClick: () => userModalOpen(true, group),
      });
    }
    if (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"])) {
      defaultItems.push({
        name: `${group?.image ? "Edit" : "Add"} icon`,
        onClick: () => setAddIconGroup(group),
      });
    }
    if (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"])) {
      defaultItems.push({
        type: "hr",
      });
      defaultItems.push({
        name: "Clone group",
        onClick: () => setCloneGroup(group),
      });
    }
    if (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"])) {
      if (defaultItems.length > 0)
        defaultItems.push({
          type: "hr",
        });

      defaultItems.push({
        name: "Delete group",
        onClick: () => setDeleteGroup(group),
        color: "text-red-500",
      });
    }

    setItems(defaultItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.me, group, workspaceDetails?.workspace_type]);

  const assignDisplayName = async (permission) => {
    try {
      const { data } = await apiRequest(
        "put",
        `/workspaces/:workspace_id/groups/:group_id/display-name`,
        {
          body: {
            permission: permission,
          },
          params: { workspace_id: workspaceId, group_id: group._id },
        },
        { showToastMessage: true, onSuccess: () => {}, onFailure: () => {} },
      );
      setSelectedGroup((prevData) => ({
        ...prevData,
        permissions: prevData.permissions.map((p, index) => {
          if (index === permission?.ordering) {
            return {
              ...p,
              _id: data?.data?._id,
              displayNameChanges: true,
              page_alias: "",
            };
          } else {
            return p;
          }
        }),
      }));
    } catch (error) {}
  };

  const handleGroupNameUpdate = async (name = " ") => {
    try {
      await apiRequest("put", `/workspaces/:workspace_id/groups/:group_id/group-name`, {
        body: { name },
        params: { workspace_id: workspaceId, group_id: group._id ? group._id : group.id },
      });
      setGroups([
        ...props?.groups.map((grp) => {
          if (group._id === grp?._id) {
            return {
              ...grp,
              name: name,
            };
          } else {
            return grp;
          }
        }),
      ]);
      // refreshGroups();
    } catch (error) {}
  };

  const [debouncedNameUpdate, debounceNameStatus] = useDebounce(handleGroupNameUpdate, 500);

  const handleChange = async (e) => {
    setName(e.target.value);
    if (e.target.value) {
      debouncedNameUpdate(e.target.value);
    }
  };

  const handleSubmit = () => {
    if (groupChanges > 0) {
      saveGroupChanges();
    }
  };

  return (
    <>
      {!viewOnly && (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["content_page_update"])) && (
        <div className={classNames("flex w-full justify-end gap-x-4 rounded border transition-all", groupChanges > 0 ? "mb-3 h-auto border-gray-200 bg-gray-50/50 p-3 py-2 opacity-100" : "h-0 overflow-hidden border-transparent opacity-0")}>
          <Button
            version="gray"
            onClick={onClear}
            disabled={groupChanges < 1}>
            Undo
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={groupChanges < 1}>
            Save
          </Button>
        </div>
      )}
      <div className="relative flex max-w-full flex-col rounded-md border border-gray-200 shadow lg:min-h-[400px]">
        <div className="flex items-center justify-between gap-10 px-6 py-4">
          <div className="relative flex w-full items-center gap-x-2">
            {group.image && (
              <div className="mb-2 mr-2 h-10 w-10 overflow-hidden p-1 py-2">
                {isSVGString(group.image) ? (
                  <div dangerouslySetInnerHTML={{ __html: group.image }} />
                ) : (
                  <Image
                    image={group.image}
                    failImage={noFavicon}
                    alt={"Groups"}
                  />
                )}
              </div>
            )}
            {workspaceDetails?.workspace_type === "IFRAME_EMBED" ? (
              <Button
                version="default"
                className="group relative flex w-full cursor-default items-center">
                {authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"]) && !viewOnly ? (
                  <>
                    <div className={classNames("py-.5 absolute -right-3 -top-3 flex items-center gap-x-2 overflow-hidden p-1 px-1 font-light transition-all duration-75", loadingColors[debounceNameStatus])}>
                      {debounceNameStatus === "WAITING" && (
                        <p className="flex items-center rounded-full bg-gray-500/20">
                          <ArrowPathIcon className="h-5 w-5 animate-spin text-white" />
                        </p>
                      )}
                      {debounceNameStatus === "DONE" && (
                        <p className="flex items-center rounded-full bg-green-600/40 text-white">
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                        </p>
                      )}
                    </div>
                    <Input
                      type="text"
                      autoComplete="on"
                      name="group-name"
                      value={name}
                      onChange={handleChange}
                      inputClassNames="w-full max-w-[400px] md:max-w-[600px] xl:max-w-[800px] pr-4 focus:ring-0 focus:border-highlightColor bg-transparent hover:border-gray-400/20 border border-transparent flex items-center gap-x-2 text-base px-2 py-1 rounded transition-all duration-75 text-gray-800"
                    />
                  </>
                ) : (
                  <p className="truncate font-semibold text-gray-700">{group.name}</p>
                )}
              </Button>
            ) : (
              <p className="truncate font-semibold text-gray-700">{group.name}</p>
            )}
          </div>
          <div className="flex items-center">
            <div className="flex items-center justify-end gap-x-3">
              <div className="hidden items-center justify-end sm:flex">
                {group.users?.length > 0 && !props?.userId && (
                  <div
                    onClick={() => {
                      if (!viewOnly) {
                        userModalOpen(true, group);
                      }
                    }}
                    className={classNames("relative flex gap-x-3", !viewOnly ? "cursor-pointer hover:underline" : "")}>
                    {group.users?.length > 0 && <Members group={{ ...group, users: props?.userId ? group.users.filter((user) => user?._id === props?.userId) : group.users }} />}
                  </div>
                )}
                {group.permissions?.length > 0 && (
                  <>
                    {group.users?.length > 0 && !props?.userId && <div className="ml-4 mr-2 hidden h-[4px] w-[4px] rounded-full bg-gray-400 md:flex" />}
                    <div className="hidden w-10 md:flex">
                      <div className={classNames("relative hidden items-center gap-x-1 rounded border-transparent px-2 py-2 text-sm text-gray-400 sm:flex", !viewOnly ? "cursor-pointer hover:bg-gray-50 hover:text-gray-500 hover:shadow-sm" : "")}>
                        <WindowIcon className="h-6 w-6" />
                        <p className={classNames("absolute bottom-0 flex h-5 min-w-5 items-center justify-center rounded-full border-[1px] border-gray-100 bg-white p-1", group.permissions?.length > 10 ? "-right-2" : "right-0")}>{group.permissions?.length} </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {defaultGroup._id &&
                (authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["content_page_update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["user_update"])) &&
                !viewOnly && (
                  <QuickMenu
                    icon={
                      <div className="flex h-full items-center justify-center rounded-md px-2 py-2 text-gray-400 hover:bg-gray-50 hover:text-gray-500">
                        <Cog6ToothIcon className="h-6 w-6" />
                      </div>
                    }
                    position="LEFT"
                    items={items}
                  />
                )}
            </div>
          </div>
        </div>

        <div className="relative h-full flex-grow border-t border-gray-200 px-3 py-3 sm:px-6 sm:py-4">
          <WorkspaceGroupAuthorizeUserComponent
            group={group}
            permissions={group?.permissions || []}
            setGroup={setGroup}
            groupChanges={groupChanges}
            setGroupChanges={setGroupChanges}
            me={props.me}
            pageOptions={pageOptions}
            operatorOptions={operatorOptions}
            viewOnly={viewOnly}
            trustedAttributeArr={trustedAttributeArr}
            valueType={valueType}
            workspaceDetails={workspaceDetails}
            workspaceId={workspaceId}
            addPermission={(e, index) => {
              setGroup((preData) => ({
                ...preData,
                permissions: preData.permissions.map((p, i) => {
                  if (i === index) {
                    return e;
                  } else {
                    return p;
                  }
                }),
              }));
            }}
            updatePermission={(updatedPermission) => {
              setGroup((preData) => ({
                ...preData,
                permissions: preData.permissions.map((oldPermission) => {
                  if (oldPermission._id === updatedPermission._id) {
                    return updatedPermission;
                  } else {
                    return oldPermission;
                  }
                }),
              }));
              setGroupChanges(groupChanges + 1);
            }}
            removePermission={(e, index) => {
              setGroupChanges(groupChanges + 1);
              setGroup({
                ...group,
                permissions: group.permissions.filter((p, i) => i !== index),
              });
            }}
            assignDisplayName={assignDisplayName}
            authorizeUserComponentAccessPermission={authorizeUserComponentAccess(props.me, workspaceId, "group", ["content_page_update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"])}
          />
        </div>
        {group?._id && !viewOnly && (authorizeUserComponentAccess(props.me, workspaceId, "group", ["content_page_update"]) || authorizeUserComponentAccess(props.me, workspaceId, "group", ["update"])) && (
          <div className="flex h-full w-full items-center justify-end p-4">
            <Button
              version="secondary"
              onClick={() => setGroup({ ...group, permissions: [...group.permissions, pageJson] })}>
              <PlusIcon className="h-4 w-4 stroke-[3px]" />
              Add page
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    me: state.auth.user,
  };
};

export default connect(mapStateToProps, {})(WorkspaceGroupItem);
